import { Box } from '@mui/material'
import { LatLngExpression } from 'leaflet'
import { FC, useMemo } from 'react'
import { useGetMooringsForMap } from 'src/api/moorings'
import { IMooringType, IPositionType } from 'src/generated-types'
import { MapComponent } from '../../components/map/MapComponent'
import { IDeviationType } from 'src/generated-types/deviation-type'

type Props = {
  customerId?: EntityId
  localityId?: EntityId
  mooringId?: number
  positionId?: number
  deviationType?: IDeviationType
}

/**
 * Facility map for the deviation detail page: renders the deviation's mooring and
 * highlights the cage/position the deviation is on (other positions dimmed).
 * Mirrors SelectedPositionMap, but clones the mooring so we never mutate the query cache.
 */
export const DeviationLocationMap: FC<Props> = ({ customerId, localityId, mooringId, positionId, deviationType }) => {
  const { data: moorings } = useGetMooringsForMap(customerId as EntityId, localityId as EntityId, {
    isWithPositionData: true,
  })

  const selectedMooring = useMemo(() => {
    const mooring = (moorings ?? []).find(m => m.id === mooringId)
    if (!mooring) return undefined
    return {
      ...mooring,
      // Dim every position except the deviation's so it stands out.
      positions: mooring.positions.map(p => ({
        ...p,
        isTransparent: positionId != null && p.id !== positionId,
      })),
    }
  }, [moorings, mooringId, positionId])

  const center = useMemo<LatLngExpression | undefined>(() => {
    if (!selectedMooring || !selectedMooring.positions || selectedMooring.positions.length === 0) return undefined

    const target = selectedMooring.positions.find(p => p.id === positionId) ?? selectedMooring.positions[0]

    if (!target) return undefined

    let centerLat = 0
    let centerLng = 0

    if (
      target.type === IPositionType.MooringLine &&
      target.connectedPositions &&
      target.connectedPositions.length > 0
    ) {
      centerLat = (target.latitude + (target.connectedPositions[0]?.latitude ?? 0)) / 2
      centerLng = (target.longitude + (target.connectedPositions[0]?.longitude ?? 0)) / 2
    } else if (target.connectedPositions && target.connectedPositions.length > 0) {
      centerLat = target.connectedPositions[0]?.latitude ?? 0
      centerLng = target.connectedPositions[0]?.longitude ?? 0
    } else {
      centerLat = target.latitude || 0
      centerLng = target.longitude || 0
    }

    if (!centerLat || !centerLng) return undefined
    return [centerLat, centerLng]
  }, [selectedMooring, positionId])

  if (!selectedMooring || !center) return null

  return (
    <Box sx={{ height: 170, width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
      <MapComponent
        moorings={[selectedMooring]}
        center={deviationType == IDeviationType.Mooring ? undefined : center}
        zoom={16}
        mooringType={selectedMooring.type as IMooringType}
        zoomControl={false}
        scrollWheelZoom={false}
        attributionControl={false}
        layerControl={false}
      />
    </Box>
  )
}
